import { useCallback, useRef, useState } from 'react';
import { ConfirmDialog } from './ConfirmDialog';

interface ConfirmOptions {
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Adds a third button; `confirm` then resolves 'alt' when it is chosen. */
  altLabel?: string;
  confirmVariant?: 'primary' | 'danger';
}

/** confirm → true, cancel/Escape/overlay → false, third button → 'alt'. */
export type ConfirmResult = boolean | 'alt';

/**
 * Promise-based confirmation, a drop-in for `window.confirm` that renders the
 * styled ConfirmDialog instead of the browser's blocking prompt.
 *
 *   const { confirm, dialog } = useConfirm();
 *   if (await confirm({ message: 'Delete this?', confirmVariant: 'danger' })) …
 *   // …and render {dialog} once in the component's JSX.
 *
 * The returned promise resolves true on confirm, false on cancel/escape/overlay,
 * and 'alt' when the optional third button is chosen.
 */
export function useConfirm() {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions>({ message: '' });
  // Held in a ref so resolving isn't captured against a stale render.
  const resolveRef = useRef<((value: ConfirmResult) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    setOpts(options);
    setOpen(true);
    return new Promise<ConfirmResult>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const settle = useCallback((value: ConfirmResult) => {
    setOpen(false);
    resolveRef.current?.(value);
    resolveRef.current = null;
  }, []);

  const dialog = (
    <ConfirmDialog
      isOpen={open}
      message={opts.message}
      confirmLabel={opts.confirmLabel}
      cancelLabel={opts.cancelLabel}
      altLabel={opts.altLabel}
      onAlt={opts.altLabel ? () => settle('alt') : undefined}
      confirmVariant={opts.confirmVariant}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirm, dialog };
}
